import "@/App.css";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { LanguageProvider } from "@/context/LanguageContext";
import { TTSProvider } from "@/context/TTSContext";
import { PlaceContextProvider } from "@/context/PlaceContext";
import { AuthProvider } from "@/context/AuthContext";
import { Layout } from "@/components/site/Layout";
import { HomesLayout } from "@/components/homes/HomesLayout";
import { Toaster } from "@/components/ui/sonner";
import { AskUSAPulse } from "@/components/site/AskUSAPulse";
import Home from "@/pages/Home";
import ExploreStates from "@/pages/ExploreStates";
import RegionHub from "@/pages/RegionHub";
import SymbolCompare from "@/pages/SymbolCompare";
import FlagsGallery from "@/pages/FlagsGallery";
import FlagsQuiz from "@/pages/FlagsQuiz";
import PlaceProfile from "@/pages/PlaceProfile";
import SearchResults from "@/pages/SearchResults";
import VerticalPage from "@/pages/VerticalPage";
import Atlas from "@/pages/Atlas";
import People from "@/pages/People";
import PersonProfile from "@/pages/PersonProfile";
import Presidents from "@/pages/Presidents";
import ArticlePage from "@/pages/ArticlePage";
import AdminCMS from "@/pages/AdminCMS";
import AuthCallback from "@/pages/AuthCallback";
import InstantCities from "@/pages/InstantCities";
import StateCities from "@/pages/StateCities";
import HomesLanding from "@/pages/homes/HomesLanding";
import HomesSearch from "@/pages/homes/HomesSearch";
import PropertyPage from "@/pages/homes/PropertyPage";
import HomesPlace from "@/pages/homes/HomesPlace";
import AgentsDirectory from "@/pages/homes/AgentsDirectory";
import AgentProfile from "@/pages/homes/AgentProfile";
import AISearch from "@/pages/homes/AISearch";
import HomesSaved from "@/pages/homes/HomesSaved";
import AgentCardStudio from "@/pages/homes/AgentCardStudio";
import RelocationHub from "@/pages/homes/RelocationHub";
import RelocationGuide from "@/pages/homes/RelocationGuide";
import AgentSubscribe from "@/pages/homes/AgentSubscribe";
import SubscribeSuccess from "@/pages/homes/SubscribeSuccess";
import AgentPortal from "@/pages/homes/AgentPortal";
import AgentMicrosite from "@/pages/homes/AgentMicrosite";
import CityCompare from "@/pages/homes/CityCompare";
import MarketingCenter from "@/pages/homes/MarketingCenter";
import AgentCRM from "@/pages/homes/AgentCRM";
import ListProperty from "@/pages/homes/ListProperty";

function AppRoutes() {
  const location = useLocation();
  // Process Emergent OAuth return before anything else (read hash from router location)
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  const isHomesHost =
    typeof window !== "undefined" && /(^|\.)homespulse\./i.test(window.location.hostname);
  const isHomes = location.pathname.startsWith("/homes");

  // HomesPulse.pro domain lands on the HomesPulse experience
  if (isHomesHost && location.pathname === "/") return <Navigate to="/homes" replace />;

  if (isHomes) {
    return (
      <HomesLayout>
        <Routes>
          <Route path="/homes" element={<HomesLanding />} />
          <Route path="/homes/search" element={<HomesSearch />} />
          <Route path="/homes/advisor" element={<AISearch />} />
          <Route path="/homes/saved" element={<HomesSaved />} />
          <Route path="/homes/property/:id" element={<PropertyPage />} />
          <Route path="/homes/place/:geoId" element={<HomesPlace />} />
          <Route path="/homes/agents" element={<AgentsDirectory />} />
          <Route path="/homes/agents/:id" element={<AgentProfile />} />
          <Route path="/homes/card-studio" element={<AgentCardStudio />} />
          <Route path="/homes/relocation" element={<RelocationHub />} />
          <Route path="/homes/relocation/:geoId" element={<RelocationGuide />} />
          <Route path="/homes/compare" element={<CityCompare />} />
          <Route path="/homes/marketing" element={<MarketingCenter />} />
          <Route path="/homes/crm" element={<AgentCRM />} />
          <Route path="/homes/list-property" element={<ListProperty />} />
          <Route path="/homes/subscribe" element={<AgentSubscribe />} />
          <Route path="/homes/subscribe/success" element={<SubscribeSuccess />} />
          <Route path="/homes/agent-portal" element={<AgentPortal />} />
          <Route path="/homes/agent/:slug" element={<AgentMicrosite />} />
        </Routes>
      </HomesLayout>
    );
  }

  return (
    <>
      <Layout>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/explore/states" element={<ExploreStates />} />
          <Route path="/region/:key" element={<RegionHub />} />
          <Route path="/symbols/compare" element={<SymbolCompare />} />
          <Route path="/flags" element={<FlagsGallery />} />
          <Route path="/flags/quiz" element={<FlagsQuiz />} />
          <Route path="/explore/:stateSlug/cities" element={<StateCities />} />
          <Route path="/cities" element={<InstantCities />} />
          <Route path="/atlas" element={<Atlas />} />
          <Route path="/people" element={<People />} />
          <Route path="/presidents" element={<Presidents />} />
          <Route path="/people/:id" element={<PersonProfile />} />
          <Route path="/place/*" element={<PlaceProfile />} />
          <Route path="/learn/:geoId/:module" element={<ArticlePage />} />
          <Route path="/admin" element={<AdminCMS />} />
          <Route path="/search" element={<SearchResults />} />
          <Route path="/vertical/:slug" element={<VerticalPage />} />
        </Routes>
      </Layout>
      <AskUSAPulse />
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <LanguageProvider>
          <TTSProvider>
            <AuthProvider>
              <PlaceContextProvider>
                <AppRoutes />
                <Toaster position="top-center" richColors />
              </PlaceContextProvider>
            </AuthProvider>
          </TTSProvider>
        </LanguageProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
